"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  CardHeader,
  Col,
  Row,
  Modal,
  Button as BootstrapButton,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from '../../common/Toast/toast';
import PopupDone, { usePopupDone } from '../../common/PopupDone/PopupDone';

import TextInput from "../../common/FormFields/TextInput.jsx";
import CustomAsyncDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
// The trainer area's single stylesheet. Needed here for the standalone
// /trainer-addquiz route, which renders this without the list around it.
import "../TrainerDashboard.css";

// Services
import {
  createQuiz,
  updateQuiz,
  getQuizById,
} from "../../../../services/QuizService.js";
import { getcourses } from "../../../../services/CourseModule";
import { getBatchesWithFilters } from "../../../../services/BatchService";
import { createBatchQuizHistory } from "../../../../services/BatchQuizHistoryService.js";
import {
  addMcq,
  getMcqsByCourse,
} from "../../../../services/McqService.js";
import { getMe } from "../../../../services/AuthService";
import {
  getTrainers,
  getTrainerByUserId,
} from "../../../../services/TrainerService";
import { useAuth } from "../../../../context/AuthContext";
import Button from "../../common/Button/Button.jsx";
import DatePickerInput from "../../common/FormFields/DatePickerInput.jsx";

// The quiz form asks three things in turn: what it is, when it runs,
// and which questions are on it.
const QUIZ_STEPS = [
  { label: "Details", title: "What is the quiz?", hint: "Name it and say which course it belongs to." },
  { label: "Schedule", title: "When does it run?", hint: "The window it is open for, how long students get, and what it is out of." },
  { label: "Questions", title: "Which questions?", hint: "Pick the questions from that course." },
];

/* Assigning is a fourth question, and an optional one -- a quiz can be
   written now and given out later. Offered only when CREATING: an
   assignment is its own record with its own screen (Quiz to Batch), and
   editing a quiz must not quietly mint a second one. */
const ASSIGN_STEP = {
  label: "Batches",
  title: "Who takes it?",
  hint: "Optional. Assign it to batches now, or leave this and use Quiz to Batch later.",
};

const quizFilled = (v) => String(v ?? "").trim().length > 0;

// Whether a step has been answered well enough to move on. The values
// are checked as well as Formik's `errors`, because `errors` is empty
// until validation has run and an untouched step would otherwise look
// valid. `trainerRequired` mirrors the schema: only admins pick one.
const quizStepComplete = (step, values = {}, errors = {}, trainerRequired = true) => {
  if (step === 1) {
    const trainerOk = trainerRequired
      ? quizFilled(values.selectedTrainer) && !errors.selectedTrainer
      : !errors.selectedTrainer;
    return (
      quizFilled(values.title) &&
      !errors.title &&
      quizFilled(values.courseId) &&
      !errors.courseId &&
      trainerOk
    );
  }

  if (step === 2) {
    return (
      quizFilled(values.startDate) &&
      quizFilled(values.endDate) &&
      !errors.startDate &&
      !errors.endDate &&
      Number(values.time) > 0 &&
      !errors.time &&
      Number(values.marks) > 0 &&
      !errors.marks
    );
  }

  if (step === 3) {
    return Array.isArray(values.mcqIds) && values.mcqIds.length > 0 && !errors.mcqIds;
  }

  return true;
};

const quizStepMissing = (step, values = {}, errors = {}, trainerRequired = true) => {
  const out = [];
  const need = (unanswered, label) => {
    if (unanswered) out.push(label);
  };

  if (step === 1) {
    need(!quizFilled(values.title), "a quiz title");
    need(!quizFilled(values.courseId), "a course");
    if (trainerRequired) need(!quizFilled(values.selectedTrainer), "a trainer");
  }

  if (step === 2) {
    const opens = quizFilled(values.startDate);
    const closes = quizFilled(values.endDate);
    need(!opens, "when it opens");
    need(!closes, "when it closes");
    need(!(Number(values.time) > 0), "a duration in minutes");
    need(!(Number(values.marks) > 0), "total marks");
    /* The one rule a value cannot answer on its own. Only once BOTH
       ends are set, or a half-filled window reports an ordering fault
       the reader has not had the chance to commit yet. */
    if (opens && closes && errors.endDate) out.push(String(errors.endDate));
  }

  if (step === 3) {
    need(
      !(Array.isArray(values.mcqIds) && values.mcqIds.length > 0),
      "at least one question",
    );
  }

  return out;
};

/* Why the button is dead, said where the button is. A disabled control
   with no reason beside it is the thing that sends a reader hunting for
   a bug -- and the field errors cannot do this job on their own, because
   they only appear once a field has been TOUCHED, and the fields that
   stop you are the ones you never touched. On the last step the save
   button answers for every step at once, so it names the step too. */
const QuizStepIssues = ({ step, steps, values, errors, trainerRequired }) => {
  const onLastStep = step >= steps.length;
  const blocking = (onLastStep ? [1, 2, 3] : [step])
    .map((n) => ({ n, missing: quizStepMissing(n, values, errors, trainerRequired) }))
    .filter((b) => b.missing.length > 0);

  if (!blocking.length) return null;

  const here = blocking.length === 1 && blocking[0].n === step;

  return (
    <p className="att-note att-note--warn" role="status">
      {here
        ? `Still needed: ${blocking[0].missing.join(", ")}.`
        : `Go back and finish \u2014 ${blocking
            .map((b) => `${steps[b.n - 1].label}: ${b.missing.join(", ")}`)
            .join("; ")}.`}
    </p>
  );
};

// Rendered two ways: as the /trainer-addquiz route, and inside the quiz
// list's own dialog. With none of the props below it reads the URL and
// navigates on finish, exactly as the route always did.
const AddQuiz = ({
  // Shown inside the quiz list's dialog rather than as a page: the
  // dialog draws the heading and the way out.
  embedded = false,
  // The quiz to edit. Falls back to the :id route param.
  quizId,
  // Called after a successful save, and on cancel, instead of
  // navigating away.
  onDone,
  onCancel,
  // Told the current step's question, so the dialog can title itself.
  onStepChange,
}) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = quizId ?? routeId;

  // Every exit goes through these, so the component never has to know
  // which shell it is in.
  const leaveQuiz = useCallback(() => {
    if (onCancel) onCancel();
    else navigate("/trainer-listquiz");
  }, [onCancel, navigate]);

  const finishQuiz = useCallback(() => {
    if (onDone) onDone();
    else navigate("/trainer-listquiz");
  }, [onDone, navigate]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [mcqOptions, setMcqOptions] = useState([]);
  const [showAddMcqModal, setShowAddMcqModal] = useState(false);
  // An MCQ added from its popup is confirmed in that popup, which closes
  // itself a few seconds later.
  const { done: mcqDone } = usePopupDone(showAddMcqModal);
  const [selectedCourseForMcq, setSelectedCourseForMcq] = useState("");
  const [isLoadingMcqs, setIsLoadingMcqs] = useState(false);
  const [trainerId, setTrainerId] = useState("");
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTrainerForQuiz, setSelectedTrainerForQuiz] = useState(null);

  // Dropdown pagination states
  const [coursePage, setCoursePage] = useState(1);
  const [trainerPage, setTrainerPage] = useState(1);
  const [hasMoreCourses, setHasMoreCourses] = useState(true);
  const [hasMoreTrainers, setHasMoreTrainers] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [trainerSearchTerm, setTrainerSearchTerm] = useState("");
  const [courseHasLoaded, setCourseHasLoaded] = useState(false);
  const [trainerHasLoaded, setTrainerHasLoaded] = useState(false);

  // MCQ pagination states
  const [mcqPage, setMcqPage] = useState(1);
  const [hasMoreMcqs, setHasMoreMcqs] = useState(true);
  const [loadingMoreMcqs, setLoadingMoreMcqs] = useState(false);
  const [mcqSearchTerm, setMcqSearchTerm] = useState("");
  const isFetchingMcqs = useRef(false);
  const isFetchingMcqsMore = useRef(false);

  // Batch picker, for the optional assign step.
  const [batchOptions, setBatchOptions] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchPage, setBatchPage] = useState(1);
  const [hasMoreBatches, setHasMoreBatches] = useState(true);
  const [batchSearchTerm, setBatchSearchTerm] = useState("");
  const isFetchingBatches = useRef(false);

  const [initialValues, setInitialValues] = useState({
    title: "",
    courseId: "",
    mcqIds: [],
    startDate: "",
    endDate: "",
    time: 60,
    /* Empty, not 0. Step two cannot be left until marks are above
       zero (quizStepComplete), so a field pre-filled with 0 read as
       answered while silently holding Continue disabled -- and the
       "Total marks is required" error only appears once the field has
       been touched, which a reader who thinks it is filled never does.
       60 stays on time: that is a real default, not a placeholder. */
    marks: "",
    selectedTrainer: "",
    // Empty means "do not assign", which is the default.
    batchIds: [],
  });

  const [mcqFormData, setMcqFormData] = useState({
    question: "",
    option1: "",
    option2: "",
    option3: "",
    option4: "",
    correctAnswer: "",
    courseId: "",
    selectedTrainer: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [quizStep, setQuizStep] = useState(1);
  const [quizShownStep, setQuizShownStep] = useState(1);
  const [quizPhase, setQuizPhase] = useState("in");

  useEffect(() => {
    if (onStepChange) onStepChange(quizSteps[quizStep - 1], quizStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizStep]);

  useEffect(() => {
    if (quizShownStep === quizStep) return undefined;
    setQuizPhase("out");
    const t = setTimeout(() => {
      setQuizShownStep(quizStep);
      setQuizPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [quizStep, quizShownStep]);
  const [isSubmittingMcq, setIsSubmittingMcq] = useState(false);
  const isEditMode = Boolean(id);
  // The assign step exists only while creating -- see ASSIGN_STEP.
  const quizSteps = isEditMode ? QUIZ_STEPS : [...QUIZ_STEPS, ASSIGN_STEP];

  const hasShownToast = useRef(false);
  const getMeCalled = useRef(false);
  const { hasPermission } = useAuth();
  const isFetchingCourses = useRef(false);
  const isFetchingTrainers = useRef(false);

  useEffect(() => {
    if (!hasPermission("trainer-listquiz", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to add quizzes.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  const validationSchema = Yup.object({
    title: Yup.string()
      .required("Quiz title is required")
      .min(3, "Title must be at least 3 characters"),
    courseId: Yup.string().required("Course is required"),
    mcqIds: Yup.array().min(1, "At least one MCQ is required"),
    startDate: Yup.date().required("Start time is required"),
    endDate: Yup.date()
      .required("End time is required")
      .min(Yup.ref("startDate"), "End time must be after start time"),
    time: Yup.number()
      .required("time is required")
      .min(1, "time must be at least 1 minute"),
    marks: Yup.number()
      .required("Total marks is required")
      .min(1, "Total marks must be at least 1"),
    selectedTrainer: isAdmin
      ? Yup.string().required("Please select a trainer")
      : Yup.string(),
  });

  const mcqValidationSchema = Yup.object({
    question: Yup.string()
      .required("Question is required")
      .min(10, "Question must be at least 10 characters"),
    option1: Yup.string().required("Option 1 is required"),
    option2: Yup.string().required("Option 2 is required"),
    option3: Yup.string().required("Option 3 is required"),
    option4: Yup.string().required("Option 4 is required"),
    correctAnswer: Yup.string().required("Correct answer is required"),
    courseId: Yup.string().required("Course is required for MCQ"),
    selectedTrainer: isAdmin
      ? Yup.string().required("Please select a trainer")
      : Yup.string(),
  });

  // Fetch courses for dropdown with pagination
  const fetchCoursesForDropdown = useCallback(
    async (page = 1, search = "") => {
      if (isFetchingCourses.current) return;
      // Page 1 is never gated on hasMoreCourses -- same closure problem
      // as the trainer list above.
      if (page > 1 && (loadingCourses || !hasMoreCourses)) return;

      isFetchingCourses.current = true;
      setLoadingCourses(true);
      try {
        const response = await getcourses(20, page, search);
        const coursesData = response.data || [];

        const newOptions = coursesData.map((course) => ({
          value: course.id,
          label: `${course.name} (${course.code})`,
        }));

        if (page === 1) {
          setCourseOptions(newOptions);
        } else {
          setCourseOptions((prev) => [...prev, ...newOptions]);
        }

        setHasMoreCourses(coursesData.length === 20);
        setCoursePage(page);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error("Error loading courses");
      } finally {
        setLoadingCourses(false);
        isFetchingCourses.current = false;
      }
    },
    [loadingCourses, hasMoreCourses],
  );

  // Fetch trainers for dropdown
  const fetchTrainersForDropdown = useCallback(
    async (page = 1, search = "") => {
      if (isFetchingTrainers.current) return;
      // Page 1 is a fresh list, so it is never gated on hasMoreTrainers:
      // that value is read from this closure, and a refresh or a search
      // sets it back to true on the same tick this call is made, which
      // the closure cannot see. This is why the picker stayed empty.
      if (page > 1 && (loadingTrainers || !hasMoreTrainers)) return;

      isFetchingTrainers.current = true;
      setLoadingTrainers(true);
      try {
        const response = await getTrainers(20, page, search);
        const trainersData = response.data || [];

        const trainerOptionsData = trainersData.map((trainer) => {
          const trainerName = trainer.trainerName || "";
          const email = trainer.traineremail || "";
          const trainerIdVal = trainer.id;

          let label = "";
          if (trainerName) {
            label = trainerName;
            if (email) {
              label += ` (${email})`;
            }
          } else if (email) {
            label = email;
          } else {
            label = `Trainer ${trainerIdVal}`;
          }

          return {
            value: trainerIdVal,
            label: label,
            email: email,
            trainerId: trainerIdVal,
            trainerData: trainer,
          };
        });

        if (page === 1) {
          setTrainerOptions(trainerOptionsData);
        } else {
          setTrainerOptions((prev) => [...prev, ...trainerOptionsData]);
        }

        setHasMoreTrainers(trainersData.length === 20);
        setTrainerPage(page);
      } catch (error) {
        console.error("Error fetching trainers:", error);
        toast.error("Error loading trainers");
      } finally {
        setLoadingTrainers(false);
        isFetchingTrainers.current = false;
      }
    },
    [loadingTrainers, hasMoreTrainers],
  );

  // Fetch current trainer data by userId for logged-in trainer
  const fetchCurrentTrainerData = useCallback(async (userId) => {
    try {
      const response = await getTrainerByUserId(userId);
      if (response && response.data) {
        const trainerIdFromData = response.data.id;
        setTrainerId(trainerIdFromData);
        return trainerIdFromData;
      }
      return null;
    } catch (error) {
      console.error("Error fetching trainer data:", error);
      return null;
    }
  }, []);

  // Refresh courses function
  const handleRefreshCourses = useCallback(async () => {
    setCourseSearchTerm("");
    setCoursePage(1);
    setHasMoreCourses(true);
    setCourseHasLoaded(false);
    setCourseOptions([]);
    await fetchCoursesForDropdown(1, "");
  }, [fetchCoursesForDropdown]);

  // Refresh trainers function
  const handleRefreshTrainers = useCallback(async () => {
    if (!isAdmin) return;
    setTrainerSearchTerm("");
    setTrainerPage(1);
    setHasMoreTrainers(true);
    setTrainerHasLoaded(false);
    setTrainerOptions([]);
    await fetchTrainersForDropdown(1, "");
  }, [isAdmin, fetchTrainersForDropdown]);

  // Fetch MCQs by course ID with pagination and trainer filter
  const fetchMcqsByCourse = useCallback(
    async (courseId, page = 1, search = "", isLoadMore = false, trainerIdParam = null) => {
      if (!courseId) return;
      
      if (isLoadMore && (isFetchingMcqsMore.current || !hasMoreMcqs)) return;
      if (!isLoadMore && isFetchingMcqs.current) return;

      if (isLoadMore) {
        isFetchingMcqsMore.current = true;
        setLoadingMoreMcqs(true);
      } else {
        isFetchingMcqs.current = true;
        setIsLoadingMcqs(true);
      }

      try {
        let trainerIdToUse = null;
        if (trainerIdParam !== null) {
          trainerIdToUse = trainerIdParam;
        } else if (isTrainer && trainerId) {
          trainerIdToUse = trainerId;
        } else if (isAdmin && selectedTrainerForQuiz?.value) {
          trainerIdToUse = selectedTrainerForQuiz.value;
        }

        const response = await getMcqsByCourse(
          courseId,
          20,
          page,
          search,
          trainerIdToUse,
        );

        let mcqsArray = [];
        let pagination = {};

        if (response?.data && Array.isArray(response.data)) {
          mcqsArray = response.data;
          pagination = response.pagination || {};
        } else if (Array.isArray(response)) {
          mcqsArray = response;
        } else {
          mcqsArray = [];
        }

        const newOptions = mcqsArray.map((mcq) => ({
          value: mcq.id,
          label: mcq.question.substring(0, 50) + "...",
          courseId: mcq.courseIds,
          marks: 1,
          trainerId: mcq.trainerId,
        }));

        if (isLoadMore) {
          setMcqOptions((prev) => {
            const existingIds = new Set(prev.map(opt => opt.value));
            const uniqueNewOptions = newOptions.filter(opt => !existingIds.has(opt.value));
            return [...prev, ...uniqueNewOptions];
          });
        } else {
          setMcqOptions(newOptions);
          setMcqPage(1);
        }

        const total = pagination.total || mcqsArray.length;
        setHasMoreMcqs(mcqsArray.length === 20 && page * 20 < total);
        setMcqPage(page);
      } catch (error) {
        console.error("Error fetching MCQs by course:", error);
        toast.error("Error loading MCQs");
      } finally {
        if (isLoadMore) {
          setLoadingMoreMcqs(false);
          isFetchingMcqsMore.current = false;
        } else {
          setIsLoadingMcqs(false);
          isFetchingMcqs.current = false;
        }
      }
    },
    [
      isTrainer,
      trainerId,
      isAdmin,
      selectedTrainerForQuiz,
      hasMoreMcqs,
    ],
  );


  // Handle dropdown open events
  const handleCourseDropdownOpen = useCallback(() => {
    if (!courseHasLoaded && !loadingCourses && courseOptions.length === 0) {
      setCourseHasLoaded(true);
      fetchCoursesForDropdown(1, "");
    }
  }, [
    courseHasLoaded,
    loadingCourses,
    fetchCoursesForDropdown,
    courseOptions.length,
  ]);

  const handleTrainerDropdownOpen = useCallback(() => {
    if (
      !trainerHasLoaded &&
      !loadingTrainers &&
      isAdmin &&
      trainerOptions.length === 0
    ) {
      setTrainerHasLoaded(true);
      fetchTrainersForDropdown(1, "");
    }
  }, [
    trainerHasLoaded,
    loadingTrainers,
    isAdmin,
    fetchTrainersForDropdown,
    trainerOptions.length,
  ]);

  // Handle course search with debounce
  const handleCourseSearch = useCallback(
    (searchTerm) => {
      setCourseSearchTerm(searchTerm);
      setCourseHasLoaded(true);
      setCoursePage(1);
      setHasMoreCourses(true);
      fetchCoursesForDropdown(1, searchTerm);
    },
    [fetchCoursesForDropdown],
  );

  // Handle trainer search
  const handleTrainerSearch = useCallback(
    (searchTerm) => {
      setTrainerSearchTerm(searchTerm);
      setTrainerHasLoaded(true);
      setTrainerPage(1);
      setHasMoreTrainers(true);
      fetchTrainersForDropdown(1, searchTerm);
    },
    [fetchTrainersForDropdown],
  );

  // Load more courses on scroll
  const handleLoadMoreCourses = useCallback(() => {
    if (hasMoreCourses && !loadingCourses && !isFetchingCourses.current) {
      fetchCoursesForDropdown(coursePage + 1, courseSearchTerm);
    }
  }, [
    hasMoreCourses,
    loadingCourses,
    fetchCoursesForDropdown,
    coursePage,
    courseSearchTerm,
  ]);

  // Load more trainers on scroll
  const handleLoadMoreTrainers = useCallback(() => {
    if (
      hasMoreTrainers &&
      !loadingTrainers &&
      !isFetchingTrainers.current &&
      isAdmin
    ) {
      fetchTrainersForDropdown(trainerPage + 1, trainerSearchTerm);
    }
  }, [
    hasMoreTrainers,
    loadingTrainers,
    isAdmin,
    fetchTrainersForDropdown,
    trainerPage,
    trainerSearchTerm,
  ]);

  // Handle load more MCQs on scroll
  const handleLoadMoreMcqs = useCallback(() => {
    if (
      selectedCourse?.value &&
      hasMoreMcqs &&
      !loadingMoreMcqs &&
      !isLoadingMcqs &&
      !isFetchingMcqsMore.current
    ) {
      let currentTrainerId = "";
      if (isTrainer && trainerId) {
        currentTrainerId = trainerId;
      } else if (isAdmin && selectedTrainerForQuiz?.value) {
        currentTrainerId = selectedTrainerForQuiz.value;
      }
      fetchMcqsByCourse(selectedCourse.value, mcqPage + 1, mcqSearchTerm, true, currentTrainerId);
    }
  }, [
    selectedCourse,
    hasMoreMcqs,
    loadingMoreMcqs,
    isLoadingMcqs,
    fetchMcqsByCourse,
    mcqPage,
    mcqSearchTerm,
    isTrainer,
    trainerId,
    isAdmin,
    selectedTrainerForQuiz,
  ]);

  // Handle MCQ search
  const handleMcqSearch = useCallback(
    (searchTerm) => {
      if (selectedCourse?.value) {
        setMcqSearchTerm(searchTerm);
        setMcqPage(1);
        setHasMoreMcqs(true);
        let currentTrainerId = "";
        if (isTrainer && trainerId) {
          currentTrainerId = trainerId;
        } else if (isAdmin && selectedTrainerForQuiz?.value) {
          currentTrainerId = selectedTrainerForQuiz.value;
        }
        fetchMcqsByCourse(selectedCourse.value, 1, searchTerm, false, currentTrainerId);
      }
    },
    [selectedCourse, fetchMcqsByCourse, isTrainer, trainerId, isAdmin, selectedTrainerForQuiz],
  );

  // Get current trainer name for read-only display
  const getCurrentTrainerName = (userData = currentUser) => {
    if (!userData) return "";
    const firstName = userData.firstName || "";
    const lastName = userData.lastName || "";
    const email = userData.email || "";

    if (firstName || lastName) {
      let name = `${firstName} ${lastName}`.trim();
      if (email) {
        name += ` (${email})`;
      }
      return name;
    }
    return email;
  };

  // Get trainer ID by email or userId for admin
  const getTrainerIdBySelection = (selectedTrainerValue) => {
    if (isTrainer && trainerId) {
      return trainerId;
    }

    if (isAdmin && selectedTrainerValue) {
      const selectedOption = trainerOptions.find(
        (opt) => opt.value === selectedTrainerValue,
      );
      return selectedOption?.value || selectedTrainerValue;
    }

    return "";
  };

  // Initialize component with user data
  const initializeUser = useCallback(async () => {
    if (getMeCalled.current) return;
    getMeCalled.current = true;

    try {
      const userProfile = await getMe();
      const userData = userProfile.data;
      const userId = userData.id || userData._id || userData.userId;

      if (!userId) {
        toast.error("Unable to fetch user information");
        return;
      }

      setCurrentUser(userData);

      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminUser = profileLabel === "admin" || userData.isSuperAdmin;

      setIsTrainer(isTrainerUser);
      setIsAdmin(isAdminUser);

      if (isTrainerUser) {
        const trainerDataId = await fetchCurrentTrainerData(userId);
        if (trainerDataId) {
          setTrainerId(trainerDataId);
          setMcqFormData((prev) => ({
            ...prev,
            selectedTrainer: trainerDataId,
          }));
          setInitialValues((prev) => ({
            ...prev,
            selectedTrainer: trainerDataId,
          }));
          setSelectedTrainerForQuiz({
            value: trainerDataId,
            label: getCurrentTrainerName(userData),
            email: userData.email || "",
          });
        }
      }

      if (isEditMode) {
        await fetchQuizData();
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      toast.error("Failed to initialize component");
      console.error(err);
      setIsLoading(false);
    }
  }, [isEditMode, fetchCurrentTrainerData]);

  // Fetch quiz data when in edit mode
  const fetchQuizData = useCallback(async () => {
    try {
      const response = await getQuizById(id);
      const quizData = response.data;

      const formatDateForPicker = (dateString) => {
        return new Date(dateString);
      };

      let courseId = "";
      let courseName = "";
      if (quizData.courseId && Array.isArray(quizData.courseId) && quizData.courseId.length > 0) {
        const course = quizData.courseId[0];
        courseId = course.id;
        courseName = `${course.name} (${course.code || ""})`;
      }

      if (courseId) {
        const selectedCourseOption = {
          value: courseId,
          label: courseName,
        };
        setSelectedCourse(selectedCourseOption);
        
        setCourseOptions(prev => {
          const exists = prev.some(opt => opt.value === courseId);
          if (!exists) {
            return [selectedCourseOption, ...prev];
          }
          return prev;
        });
      }

      let selectedMcqs = [];
      if (quizData.mcqIds && Array.isArray(quizData.mcqIds)) {
        selectedMcqs = quizData.mcqIds.map((mcq) => ({
          value: mcq.id,
          label: mcq.question.substring(0, 50) + "...",
          courseId: quizData.courseId?.[0]?.id,
          marks: 1,
        }));
        setMcqOptions(selectedMcqs);
      }

      let selectedTrainerValue = "";
      let selectedTrainerOption = null;
      
      if (quizData.createdBy && Array.isArray(quizData.createdBy) && quizData.createdBy.length > 0) {
        const trainer = quizData.createdBy[0];
        const trainerIdFromResponse = trainer.id;
        const trainerName = trainer.trainerName || "";
        
        selectedTrainerValue = trainerIdFromResponse;
        selectedTrainerOption = {
          value: trainerIdFromResponse,
          label: trainerName,
        };
        
        if (isAdmin) {
          setTrainerOptions(prev => {
            const exists = prev.some(opt => opt.value === trainerIdFromResponse);
            if (!exists) {
              return [selectedTrainerOption, ...prev];
            }
            return prev;
          });
        }
      }

      setSelectedTrainerForQuiz(selectedTrainerOption);

      setInitialValues({
        title: quizData.title || "",
        courseId: courseId,
        mcqIds: selectedMcqs,
        startDate: formatDateForPicker(quizData.startDate) || new Date(),
        endDate: formatDateForPicker(quizData.endDate) || new Date(),
        time: Number.parseInt(quizData.time) || 60,
        marks: Number.parseInt(quizData.marks) || 0,
        selectedTrainer: selectedTrainerValue,
        // Edit never shows the assign step, but the field stays on the
        // shape so nothing downstream has to guard for its absence.
        batchIds: [],
      });
      
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch quiz data:", error);
      toast.error("Failed to fetch quiz data");
      navigate("/trainer-listquiz");
    }
  }, [id, isAdmin]);

  // Handle course selection change - FIXED to use current trainer
  /* The batches this quiz can be given to: the ones on its course, and
     for a trainer, only their own. Same shape and page size as the
     assignment screen's own picker, which reads the same endpoint. */
  const fetchBatchesForDropdown = useCallback(
    async (courseId, page = 1, search = "", append = false, trainerIdOverride = "") => {
      if (!courseId) {
        setBatchOptions([]);
        setHasMoreBatches(false);
        return;
      }
      if (isFetchingBatches.current) return;
      if (page > 1 && !hasMoreBatches) return;
      isFetchingBatches.current = true;
      setLoadingBatches(true);
      try {
        const trainerIdParam =
          trainerIdOverride || (isTrainer ? trainerId : "");
        const resp = await getBatchesWithFilters(
          20,
          page,
          search,
          courseId,
          trainerIdParam,
        );
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        const next = rows.map((b) => ({ value: b.id, label: b.name }));
        setBatchOptions((prev) => {
          if (!append) return next;
          const seen = new Set(prev.map((o) => String(o.value)));
          return [...prev, ...next.filter((o) => !seen.has(String(o.value)))];
        });
        setHasMoreBatches(rows.length === 20);
        setBatchPage(page);
      } catch (err) {
        console.error("Error loading batches for assignment:", err);
        if (!append) setBatchOptions([]);
        setHasMoreBatches(false);
      } finally {
        isFetchingBatches.current = false;
        setLoadingBatches(false);
      }
    },
    [hasMoreBatches, isTrainer, trainerId],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCourseChange = useCallback(
    async (selectedCourse, setFieldValue) => {
      const courseId = selectedCourse ? selectedCourse.value : "";
      setSelectedCourse(selectedCourse);
      setFieldValue("courseId", courseId);

      // A batch belongs to a course, so the ones picked under the old
      // course cannot stand under the new one.
      setFieldValue("batchIds", []);
      setBatchOptions([]);
      setBatchPage(1);
      setHasMoreBatches(true);
      setBatchSearchTerm("");

      if (courseId) {
        setFieldValue("mcqIds", []);
        setMcqPage(1);
        setHasMoreMcqs(true);
        setMcqOptions([]);
        setMcqSearchTerm("");
        
        // Get the current trainer ID to pass directly
        let currentTrainerId = "";
        if (isTrainer && trainerId) {
          currentTrainerId = trainerId;
        } else if (isAdmin && selectedTrainerForQuiz?.value) {
          currentTrainerId = selectedTrainerForQuiz.value;
        }
        
        await fetchMcqsByCourse(courseId, 1, "", false, currentTrainerId);
        // The assign step's list, on the same trigger and the same
        // trainer scope as the questions above it.
        await fetchBatchesForDropdown(courseId, 1, "", false, currentTrainerId);
      } else {
        setMcqOptions([]);
        setHasMoreMcqs(false);
      }
    },
    [fetchMcqsByCourse, isTrainer, trainerId, isAdmin, selectedTrainerForQuiz],
  );

  // Handle MCQ selection change
  const handleMcqChange = useCallback((selectedMcqs, setFieldValue) => {
    setFieldValue("mcqIds", selectedMcqs);
  }, []);

  // Handle trainer selection for quiz - FIXED to refresh MCQs
  const handleTrainerChangeForQuiz = useCallback(
    async (selectedTrainer, setFieldValue) => {
      const newTrainerId = selectedTrainer ? selectedTrainer.value : "";
      
      setSelectedTrainerForQuiz(selectedTrainer);
      setFieldValue("selectedTrainer", newTrainerId);
      
      // If a course is selected, refresh MCQs with the new trainer ID
      if (selectedCourse?.value) {
        setMcqOptions([]);
        setMcqPage(1);
        setHasMoreMcqs(true);
        setMcqSearchTerm("");
        
        isFetchingMcqs.current = false;
        isFetchingMcqsMore.current = false;
        
        // Pass the new trainer ID directly
        await fetchMcqsByCourse(selectedCourse.value, 1, "", false, newTrainerId);
      }
    },
    [selectedCourse, fetchMcqsByCourse],
  );

  // Handle Add MCQ
  const handleAddMcq = useCallback(async () => {
    try {
      await mcqValidationSchema.validate(mcqFormData);
      setIsSubmittingMcq(true);

      let trainerIdToUse = "";
      if (isTrainer) {
        trainerIdToUse = trainerId;
      } else if (isAdmin) {
        trainerIdToUse = mcqFormData.selectedTrainer;
      }

      if (!trainerIdToUse) {
        toast.error("Trainer ID is required");
        return;
      }

      const correctAnswerNumber = parseInt(mcqFormData.correctAnswer, 10);

      const response = await addMcq({
        question: mcqFormData.question,
        option1: mcqFormData.option1,
        option2: mcqFormData.option2,
        option3: mcqFormData.option3,
        option4: mcqFormData.option4,
        correctAnswer: correctAnswerNumber,
        courseIds: selectedCourseForMcq,
        trainerId: trainerIdToUse,
      });

      toast.success("MCQ added successfully!");

      const newMcqOption = {
        value: response.data.id,
        label: `${mcqFormData.question.substring(0, 50)}...`,
        courseId: selectedCourseForMcq,
        marks: 1,
      };

      if (selectedCourse?.value === selectedCourseForMcq) {
        setMcqOptions((prev) => [newMcqOption, ...prev]);
      }

      setMcqFormData({
        question: "",
        option1: "",
        option2: "",
        option3: "",
        option4: "",
        correctAnswer: "",
        courseId: "",
        selectedTrainer: isTrainer ? trainerId : "",
      });
      setSelectedCourseForMcq("");
    } catch (error) {
      if (error.name === "ValidationError") {
        toast.error(error.message);
      } else {
        console.error("Error adding MCQ:", error);
        toast.error("Error adding MCQ. Please try again.");
      }
    } finally {
      setIsSubmittingMcq(false);
    }
  }, [
    mcqFormData,
    isTrainer,
    trainerId,
    isAdmin,
    selectedCourseForMcq,
    mcqValidationSchema,
    selectedCourse,
  ]);

  // Initial load
  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  // The house loading state, in the form's own frame. This was a
  // react-bootstrap <Card> around Bootstrap's .spinner-border with an
  // .sr-only label -- and this project deleted Bootstrap, so .sr-only was
  // inert and its "Loading..." rendered as visible words INSIDE the div
  // that MockInterview.css's unscoped .spinner-border still spins. The
  // result was a rotating word, top left, every time the edit dialog
  // opened. .pt-4 and .text-center were dead too, which is why it sat
  // against the left edge.
  if (isLoading) {
    return (
      <div className={embedded ? "att-embed att-embed--picker" : "pg"}>
        <div className="pg-state">
          <span className="pg-spinner" aria-hidden="true" />
          <p className="pg-state-text">Loading quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "att-embed att-embed--picker" : "pg"}>

      {/* The dialog draws its own title and its way out, so the page
          header is only for the standalone route. */}
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h4 className="pg-title">
              {isEditMode ? "Edit quiz" : "Create quiz"}
            </h4>
          </div>
          <div className="pg-head-actions">
            <button type="button" className="pg-btn" onClick={leaveQuiz}>
              All quizzes
            </button>
          </div>
        </header>
      )}


        <Formik
          key={isEditMode ? `edit-${id}` : "create"}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={isEditMode}
          onSubmit={async (values, { resetForm, setSubmitting }) => {
            try {
              setSubmitting(true);

              let trainerIdToUse = "";
              if (isTrainer) {
                trainerIdToUse = trainerId;
              } else if (isAdmin) {
                trainerIdToUse = getTrainerIdBySelection(
                  values.selectedTrainer,
                );
              }

              if (!trainerIdToUse) {
                toast.error("Trainer ID is required to create quiz");
                setSubmitting(false);
                return;
              }

              const quizData = {
                courseId: values.courseId,
                title: values.title,
                mcqIds: values.mcqIds.map((mcq) => mcq.value).join(","),
                time: values.time.toString(),
                startDate: new Date(values.startDate).toISOString(),
                endDate: new Date(values.endDate).toISOString(),
                marks: values.marks.toString(),
                createdBy: trainerIdToUse,
                updatedBy: trainerIdToUse,
              };

              if (isEditMode) {
                await updateQuiz(id, quizData);
                // Embedded, the list's dialog shows this in place of the form.
                toast.success("Quiz updated successfully!");
                finishQuiz();
              } else {
                const created = await createQuiz(quizData);
                const newQuizId =
                  created?.data?.id || created?.data?._id || created?.id || "";

                const chosenBatches = Array.isArray(values.batchIds)
                  ? values.batchIds.map((b) => b.value).filter(Boolean)
                  : [];

                if (chosenBatches.length && newQuizId) {
                  /* The same payload the assignment screen posts, so a
                     record made here is indistinguishable from one made
                     there -- and appears in that list straight away. The
                     window is the quiz's own, which is what Schedule
                     just asked for. */
                  try {
                    await createBatchQuizHistory({
                      trainerId: trainerIdToUse,
                      courseId: values.courseId,
                      batchIds: chosenBatches.join(","),
                      quizId: newQuizId,
                      startDate: quizData.startDate,
                      endDate: quizData.endDate,
                      createdBy: trainerIdToUse,
                      updatedBy: trainerIdToUse,
                    });
                    toast.success(
                      `Quiz created and assigned to ${chosenBatches.length} batch${
                        chosenBatches.length === 1 ? "" : "es"
                      }.`,
                    );
                  } catch (assignError) {
                    console.error("Quiz created, assignment failed:", assignError);
                    toast.error(
                      "Quiz created, but assigning it to the batches failed. Assign it from Quiz to Batch.",
                    );
                  }
                } else {
                  if (chosenBatches.length && !newQuizId) {
                    // Created, but the response carried no id to assign
                    // against. Say so rather than silently dropping it.
                    toast.error(
                      "Quiz created, but it could not be assigned automatically. Assign it from Quiz to Batch.",
                    );
                  } else {
                    toast.success("Quiz created successfully!");
                  }
                }
                resetForm();
                finishQuiz();
              }
            } catch (error) {
              console.error(
                `Error ${isEditMode ? "updating" : "creating"} quiz:`,
                error,
              );
              toast.error(
                `Error ${isEditMode ? "updating" : "creating"} quiz. Please try again.`,
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            setFieldValue,
            isSubmitting,
            errors,
            touched,
            handleSubmit,
          }) => (
            <form className="mcq-form" noValidate onSubmit={handleSubmit}>
              <ol className="att-steps">
                {quizSteps.map((s, i) => {
                  const n = i + 1;
                  const state =
                    n === quizStep ? "is-now" : n < quizStep ? "is-done" : "";
                  return (
                    <li className={`att-step-tab ${state}`.trim()} key={s.label}>
                      <button
                        type="button"
                        disabled={n === quizStep || (!isEditMode && n > quizStep)}
                        onClick={() => setQuizStep(n)}
                      >
                        <span className="att-step-n">{n}</span>
                        {s.label}
                      </button>
                    </li>
                  );
                })}
              </ol>

              <div className={`att-stage is-${quizPhase}`}>
                <div className="att-stage-inner" key={quizShownStep}>
                  {quizShownStep === 1 && (
                    <section className="att-step">
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="quiz-title">
                          Quiz title
                        </label>
                        <input
                          id="quiz-title"
                          type="text"
                          className="pg-input"
                          placeholder="Enter quiz title"
                          value={values.title}
                          onChange={(e) => setFieldValue("title", e.target.value)}
                        />
                        {touched.title && errors.title && (
                          <p className="att-error">{errors.title}</p>
                        )}
                      </div>

                      <div className="mcq-options">
                        <div className="pg-field">
                          <label className="pg-label">Course</label>
                          <CustomAsyncDropdown
                            options={courseOptions}
                            value={selectedCourse ? [selectedCourse] : []}
                            onChange={(selectedOptions) => {
                              const selected =
                                selectedOptions && selectedOptions.length > 0
                                  ? selectedOptions[0]
                                  : null;
                              handleCourseChange(selected, setFieldValue);
                            }}
                            placeholder="Select course..."
                            isMulti={false}
                            isSearchable={true}
                            searchInControl={true}
                            onMenuOpen={handleCourseDropdownOpen}
                            onInputChange={handleCourseSearch}
                            onMenuScrollToBottom={handleLoadMoreCourses}
                            onRefresh={handleRefreshCourses}
                            isLoading={loadingCourses}
                          />
                          {touched.courseId && errors.courseId && (
                            <p className="att-error">{errors.courseId}</p>
                          )}
                        </div>

                        {isAdmin && (
                          <div className="pg-field">
                            <label className="pg-label">Trainer</label>
                            <CustomAsyncDropdown
                              options={trainerOptions}
                              value={
                                values.selectedTrainer
                                  ? trainerOptions.filter(
                                      (o) => o.value === values.selectedTrainer,
                                    )
                                  : []
                              }
                              onChange={(opts) => {
                                const next =
                                  Array.isArray(opts) && opts.length
                                    ? opts[opts.length - 1].value
                                    : "";
                                setFieldValue("selectedTrainer", next);
                                /* Batches are the trainer's, so the ones
                                   already listed belonged to whoever was
                                   selected before -- or to nobody, if the
                                   course was picked first. */
                                setFieldValue("batchIds", []);
                                setBatchOptions([]);
                                setBatchPage(1);
                                setHasMoreBatches(true);
                                setBatchSearchTerm("");
                                if (values.courseId) {
                                  fetchBatchesForDropdown(
                                    values.courseId,
                                    1,
                                    "",
                                    false,
                                    next,
                                  );
                                }
                              }}
                              placeholder="Select trainer..."
                              isMulti={false}
                              isSearchable={true}
                              searchInControl={true}
                              isLoading={loadingTrainers}
                              onMenuOpen={handleTrainerDropdownOpen}
                              onInputChange={handleTrainerSearch}
                              onMenuScrollToBottom={handleLoadMoreTrainers}
                              onRefresh={handleRefreshTrainers}
                            />
                            {touched.selectedTrainer &&
                              errors.selectedTrainer && (
                                <p className="att-error">
                                  {errors.selectedTrainer}
                                </p>
                              )}
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {quizShownStep === 2 && (
                    <section className="att-step">
                      <div className="mcq-options">
                        <div className="pg-field">
                          <label className="pg-label" htmlFor="quiz-start">
                            Opens
                          </label>
                          <input
                            id="quiz-start"
                            type="datetime-local"
                            className="pg-input"
                            value={values.startDate || ""}
                            onChange={(e) =>
                              setFieldValue("startDate", e.target.value)
                            }
                          />
                          {touched.startDate && errors.startDate && (
                            <p className="att-error">{errors.startDate}</p>
                          )}
                        </div>

                        <div className="pg-field">
                          <label className="pg-label" htmlFor="quiz-end">
                            Closes
                          </label>
                          <input
                            id="quiz-end"
                            type="datetime-local"
                            className="pg-input"
                            value={values.endDate || ""}
                            onChange={(e) =>
                              setFieldValue("endDate", e.target.value)
                            }
                          />
                          {touched.endDate && errors.endDate && (
                            <p className="att-error">{errors.endDate}</p>
                          )}
                        </div>

                        <div className="pg-field">
                          <label className="pg-label" htmlFor="quiz-time">
                            Duration (minutes)
                          </label>
                          <input
                            id="quiz-time"
                            type="text"
                            inputMode="numeric"
                            className="pg-input"
                            placeholder="60"
                            value={values.time}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d*$/.test(val)) {
                                setFieldValue(
                                  "time",
                                  val === "" ? "" : Number.parseInt(val, 10),
                                );
                              }
                            }}
                          />
                          {touched.time && errors.time && (
                            <p className="att-error">{errors.time}</p>
                          )}
                        </div>

                        <div className="pg-field">
                          <label className="pg-label" htmlFor="quiz-marks">
                            Total marks
                          </label>
                          <input
                            id="quiz-marks"
                            type="text"
                            inputMode="numeric"
                            className="pg-input"
                            placeholder="0"
                            value={values.marks}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d*$/.test(val)) {
                                setFieldValue(
                                  "marks",
                                  val === "" ? "" : Number.parseInt(val, 10),
                                );
                              }
                            }}
                          />
                          {touched.marks && errors.marks && (
                            <p className="att-error">{errors.marks}</p>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {quizShownStep === 3 && (
                    <section className="att-step">
                      <div className="pg-field">
                        <label className="pg-label">
                          Questions
                          <span className="att-card-count">
                            {values.mcqIds?.length || 0} chosen
                          </span>
                        </label>
                        <CustomAsyncDropdown
                          options={mcqOptions}
                          value={values.mcqIds || []}
                          onChange={(selectedOptions) =>
                            handleMcqChange(selectedOptions || [], setFieldValue)
                          }
                          placeholder={
                            isLoadingMcqs
                              ? "Loading questions..."
                              : values.courseId
                                ? "Select questions..."
                                : "Choose a course first"
                          }
                          isMulti={true}
                          isSearchable={true}
                          searchInControl={true}
                          isDisabled={!values.courseId || isLoadingMcqs}
                          onInputChange={handleMcqSearch}
                          onMenuScrollToBottom={handleLoadMoreMcqs}
                          isLoading={isLoadingMcqs || loadingMoreMcqs}
                        />
                        {touched.mcqIds && errors.mcqIds && (
                          <p className="att-error">{errors.mcqIds}</p>
                        )}
                      </div>

                      {hasPermission("course-wise-mcq", "add", "lms") && (
                        <p className="att-note">
                          Missing a question?
                          <button
                            type="button"
                            className="pg-link"
                            onClick={() => setShowAddMcqModal(true)}
                          >
                            Add a new one
                          </button>
                        </p>
                      )}
                    </section>
                  )}

                  {quizShownStep === 4 && !isEditMode && (
                    <section className="att-step">
                      <div className="pg-field">
                        <label className="pg-label">
                          Batches
                          <span className="att-card-count">
                            {values.batchIds?.length || 0} chosen
                          </span>
                        </label>
                        <CustomAsyncDropdown
                          options={batchOptions}
                          value={values.batchIds || []}
                          onChange={(opts) =>
                            setFieldValue("batchIds", opts || [])
                          }
                          placeholder={
                            values.courseId
                              ? "Select batches..."
                              : "Choose a course first"
                          }
                          isMulti={true}
                          isSearchable={true}
                          searchInControl={true}
                          isDisabled={!values.courseId}
                          isLoading={loadingBatches}
                          onMenuOpen={() =>
                            fetchBatchesForDropdown(
                              values.courseId,
                              1,
                              "",
                              false,
                              isAdmin ? values.selectedTrainer : "",
                            )
                          }
                          onInputChange={(term) => {
                            setBatchSearchTerm(term);
                            fetchBatchesForDropdown(
                              values.courseId,
                              1,
                              term,
                              false,
                              isAdmin ? values.selectedTrainer : "",
                            );
                          }}
                          onMenuScrollToBottom={() =>
                            fetchBatchesForDropdown(
                              values.courseId,
                              batchPage + 1,
                              batchSearchTerm,
                              true,
                              isAdmin ? values.selectedTrainer : "",
                            )
                          }
                        />
                      </div>

                      {/* Said plainly, because a step you can walk past
                          without answering is otherwise one a reader
                          stops at wondering what is required. */}
                      <p className="att-note">
                        {values.batchIds?.length
                          ? "The quiz will be created and given to these batches, for the window set on Schedule."
                          : loadingBatches
                            ? "Loading batches..."
                            : batchOptions.length
                              ? "Leave this empty to create the quiz without giving it out. Quiz to Batch assigns it later."
                              : "No batches on this course" +
                                (isAdmin ? " for the selected trainer" : "") +
                                ". The quiz can still be created and assigned later."}
                      </p>
                    </section>
                  )}
                </div>
              </div>

              <QuizStepIssues
                step={quizStep}
                steps={quizSteps}
                values={values}
                errors={errors}
                trainerRequired={isAdmin}
              />

              <div className="att-actions">
                {quizStep > 1 ? (
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setQuizStep(quizStep - 1)}
                  >
                    Back
                  </button>
                ) : (
                  <button type="button" className="pg-btn" onClick={leaveQuiz}>
                    Cancel
                  </button>
                )}

                {/* NEITHER BRANCH MAY BE type="submit". They sit at the
                    same JSX position, so React reconciles them as ONE
                    <button> and just patches `type`: a click on Continue
                    fires onClick, React 18 flushes the discrete update
                    synchronously, the same element becomes type="submit",
                    and the browser then runs the click's activation
                    behaviour against the type it finds NOW -- submitting.
                    Continue saved the record instead of advancing a step.
                    The submit is explicit, and the keys stop the two from
                    ever being the same node. */}
                {quizStep < quizSteps.length ? (
                  <button
                    key="quiz-next"
                    type="button"
                    className="pg-btn pg-btn--main"
                    disabled={!quizStepComplete(quizStep, values, errors, isAdmin)}
                    onClick={() => setQuizStep(quizStep + 1)}
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    key="quiz-save"
                    type="button"
                    onClick={() => handleSubmit()}
                    className="pg-btn pg-btn--main"
                    disabled={
                      isSubmitting ||
                      !quizStepComplete(1, values, errors, isAdmin) ||
                      !quizStepComplete(2, values, errors, isAdmin) ||
                      !quizStepComplete(3, values, errors, isAdmin)
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="pg-spinner att-spinner"
                          aria-hidden="true"
                        />
                        {isEditMode ? "Updating..." : "Creating..."}
                      </>
                    ) : isEditMode ? (
                      "Update quiz"
                    ) : values.batchIds?.length ? (
                      "Create and assign"
                    ) : (
                      "Create quiz"
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </Formik>

      {/* Add MCQ Modal */}
      <Modal
        show={showAddMcqModal}
        onHide={() => setShowAddMcqModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>Add New MCQ</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {mcqDone ? (
            <PopupDone
              message={mcqDone}
              text="It can now be picked for this quiz."
              onClose={() => setShowAddMcqModal(false)}
            />
          ) : (
          <>
          <Row className="mb-3">
            <Col md={12}>
              <label className="form-label">Question *</label>
              <textarea
                className="form-control"
                rows={3}
                value={mcqFormData.question}
                onChange={(e) =>
                  setMcqFormData((prev) => ({
                    ...prev,
                    question: e.target.value,
                  }))
                }
                placeholder="Enter your question here..."
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">Option 1 *</label>
              <input
                type="text"
                className="form-control"
                value={mcqFormData.option1}
                onChange={(e) =>
                  setMcqFormData((prev) => ({
                    ...prev,
                    option1: e.target.value,
                  }))
                }
                placeholder="Enter option 1"
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Option 2 *</label>
              <input
                type="text"
                className="form-control"
                value={mcqFormData.option2}
                onChange={(e) =>
                  setMcqFormData((prev) => ({
                    ...prev,
                    option2: e.target.value,
                  }))
                }
                placeholder="Enter option 2"
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">Option 3 *</label>
              <input
                type="text"
                className="form-control"
                value={mcqFormData.option3}
                onChange={(e) =>
                  setMcqFormData((prev) => ({
                    ...prev,
                    option3: e.target.value,
                  }))
                }
                placeholder="Enter option 3"
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Option 4 *</label>
              <input
                type="text"
                className="form-control"
                value={mcqFormData.option4}
                onChange={(e) =>
                  setMcqFormData((prev) => ({
                    ...prev,
                    option4: e.target.value,
                  }))
                }
                placeholder="Enter option 4"
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <label className="form-label">Select Course for MCQ *</label>
              <CustomAsyncDropdown
                options={courseOptions}
                value={
                  courseOptions.find(
                    (option) => option.value === selectedCourseForMcq,
                  )
                    ? [
                        {
                          value: selectedCourseForMcq,
                          label:
                            courseOptions.find(
                              (option) => option.value === selectedCourseForMcq,
                            )?.label || "",
                        },
                      ]
                    : []
                }
                onChange={(selectedOptions) => {
                  const selected =
                    selectedOptions && selectedOptions.length > 0
                      ? selectedOptions[0]
                      : null;
                  setSelectedCourseForMcq(selected ? selected.value : "");
                  setMcqFormData((prev) => ({
                    ...prev,
                    courseId: selected ? selected.value : "",
                  }));
                }}
                placeholder="Select course for this MCQ..."
                isMulti={false}
                isSearchable={true}
                onMenuOpen={handleCourseDropdownOpen}
                onInputChange={handleCourseSearch}
                onMenuScrollToBottom={handleLoadMoreCourses}
                onRefresh={handleRefreshCourses}
                isLoading={loadingCourses}
                menuPortalTarget={document.body}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "38px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 9999,
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    maxHeight: "250px",
                    overflowY: "auto",
                    padding: "8px 0",
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    padding: "10px 12px",
                    cursor: "pointer",
                    backgroundColor: state.isFocused ? "var(--saffron-soft)" : "white",
                    color: state.isFocused ? "var(--saffron)" : "var(--ink)",
                    "&:active": {
                      backgroundColor: "var(--saffron)",
                      color: "white",
                    },
                  }),
                }}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">Correct Answer *</label>
              <select
                className="form-control"
                value={mcqFormData.correctAnswer}
                onChange={(e) =>
                  setMcqFormData((prev) => ({
                    ...prev,
                    correctAnswer: e.target.value,
                  }))
                }
                style={{ cursor: "pointer" }}
              >
                <option value="">Select correct answer</option>
                <option value="1">{mcqFormData.option1 || "Option 1"}</option>
                <option value="2">{mcqFormData.option2 || "Option 2"}</option>
                <option value="3">{mcqFormData.option3 || "Option 3"}</option>
                <option value="4">{mcqFormData.option4 || "Option 4"}</option>
              </select>
            </Col>
            <Col md={6}>
              <label className="form-label">
                {isTrainer ? "Trainer (Read Only)" : "Select Trainer *"}
              </label>
              {isTrainer ? (
                <input
                  type="text"
                  className="form-control"
                  value={getCurrentTrainerName()}
                  readOnly
                  disabled
                  style={{ backgroundColor: "var(--paper-2)", cursor: "not-allowed" }}
                />
              ) : (
                <CustomAsyncDropdown
                  options={trainerOptions}
                  value={
                    trainerOptions.find(
                      (option) => option.value === mcqFormData.selectedTrainer,
                    )
                      ? [
                          {
                            value: mcqFormData.selectedTrainer,
                            label:
                              trainerOptions.find(
                                (option) =>
                                  option.value === mcqFormData.selectedTrainer,
                              )?.label || "",
                          },
                        ]
                      : []
                  }
                  onChange={(selectedOptions) => {
                    const selected =
                      selectedOptions && selectedOptions.length > 0
                        ? selectedOptions[0]
                        : null;
                    setMcqFormData((prev) => ({
                      ...prev,
                      selectedTrainer: selected ? selected.value : "",
                    }));
                  }}
                  placeholder="Select trainer..."
                  isMulti={false}
                  isSearchable={true}
                  onMenuOpen={handleTrainerDropdownOpen}
                  onInputChange={handleTrainerSearch}
                  onMenuScrollToBottom={handleLoadMoreTrainers}
                  onRefresh={handleRefreshTrainers}
                  isLoading={loadingTrainers}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "38px",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      maxHeight: "300px",
                      overflowY: "auto",
                      zIndex: 9998,
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      maxHeight: "250px",
                      overflowY: "auto",
                      padding: "8px 0",
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      padding: "10px 12px",
                      cursor: "pointer",
                      backgroundColor: state.isFocused ? "var(--saffron-soft)" : "white",
                      color: state.isFocused ? "var(--saffron)" : "var(--ink)",
                      "&:active": {
                        backgroundColor: "var(--saffron)",
                        color: "white",
                      },
                    }),
                  }}
                />
              )}
            </Col>
          </Row>
          </>
          )}
        </Modal.Body>

        {!mcqDone && (
        <Modal.Footer>
          <BootstrapButton
            variant="secondary"
            onClick={() => setShowAddMcqModal(false)}
          >
            Cancel
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={handleAddMcq}
            disabled={isSubmittingMcq || !selectedCourseForMcq}
            style={{ backgroundColor: "var(--saffron)", borderColor: "var(--saffron)" }}
          >
            {isSubmittingMcq ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Adding...
              </>
            ) : (
              <>
                <i className="fa fa-plus me-2"></i>
                Add MCQ
              </>
            )}
          </BootstrapButton>
        </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default AddQuiz;